import React, { useCallback, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Badge from 'components/Badge/Badge';
import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import Typography from 'components/Typography/Typography';

interface IChipListInputProps {
  items: string[];
  onItemsChange: (items: string[]) => void;
  title: string;
  description: string;
  inputLabel: string;
  placeholder?: string;
  inputTestId?: string;
  listTestId?: string;
  duplicateErrorMessage?: string;
  helperText?: string;
}

const SEARCH_ICON = (
  <FontAwesomeIcon
    icon="fa-regular fa-magnifying-glass"
    className="w-4 h-4"
  />
);

/**
 * Generic string-list input with add-on-enter and badge removal.
 *
 * @example
 * <ChipListInput
 *   items={values.tags || []}
 *   onItemsChange={handleTagsChange}
 *   title="Tags"
 *   description="Create or add tags to your offer"
 *   inputLabel="Tag Name"
 *   placeholder="Search Tag"
 *   inputTestId="bundleTagsInput"
 * />
 */
export const ChipListInput: React.FC<IChipListInputProps> = ({
  items,
  onItemsChange,
  title,
  description,
  inputLabel,
  placeholder = 'Search',
  inputTestId,
  listTestId,
  duplicateErrorMessage = "You can't add the same item twice",
  helperText = "Press 'Enter' to add a new item"
}): React.ReactElement => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleAdd = useCallback(
    (value: string): void => {
      if (items.includes(value)) {
        setError(duplicateErrorMessage);
        return;
      }

      onItemsChange([...items, value]);
      setError('');
      setInputValue('');
      inputRef.current?.blur();
    },
    [items, onItemsChange, duplicateErrorMessage]
  );

  const handleRemove = useCallback(
    (value: string): void => {
      onItemsChange(items.filter((item) => item !== value));
    },
    [items, onItemsChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        handleAdd(inputValue.trim());
      }
    },
    [inputValue, handleAdd]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setInputValue(e.target.value);
      if (items.includes(e.target.value)) {
        setError(duplicateErrorMessage);
      } else {
        setError('');
      }
    },
    [items, duplicateErrorMessage]
  );

  let inputState: 'error' | 'default' = 'default';
  if (error) {
    inputState = 'error';
  }

  return (
    <div>
      <RichText
        title={title}
        description={description}
        className="mb-5"
      />
      <div className="flex flex-col gap-5">
        <div>
          <Typography className="text-black font-medium text-sm mb-2">
            {inputLabel}
          </Typography>
          <Input
            ref={inputRef}
            value={inputValue}
            placeholder={placeholder}
            dataTestId={inputTestId}
            rightIcon={SEARCH_ICON}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            state={inputState}
            description={error || ''}
          />
          {items.length === 0 && (
            <Typography className="text-zinc-500 text-sm mt-2">
              {helperText}
            </Typography>
          )}
          {items.length > 0 && (
            <div
              className="mt-3 flex flex-row flex-wrap gap-2"
              data-testid={listTestId}
            >
              {items.map((val: string) => (
                <Badge
                  key={val}
                  isRemovable
                  onRemove={() => handleRemove(val)}
                >
                  {val}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
